import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotifications as useNotificationEvents } from '@/shared/services/socketio/hooks';
import { notificationKeys } from '../../application/hooks/useNotifications';
import { useUnreadNotificationCount } from '../../application';
import { NotificationList } from './NotificationList';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = 'notification-panel';
  const client = useQueryClient();
  const count = useUnreadNotificationCount();
  const onNotification = useCallback(() => {
    void client.invalidateQueries({ queryKey: notificationKeys.all });
  }, [client]);
  useNotificationEvents(onNotification);
  const panelRef = useRef<HTMLDivElement>(null);
  const dismiss = useCallback(() => {
    setOpen(false);
    requestAnimationFrame(() => buttonRef.current?.focus());
  }, []);
  const closeQuietly = useCallback(() => {
    setOpen(false);
  }, []);
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', handleKeyDown);
    // Move focus into the panel on open — skip disabled buttons
    requestAnimationFrame(() => {
      const first = panelRef.current?.querySelector<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])');
      (first ?? panelRef.current)?.focus();
    });
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, dismiss]);
  return (
    <>
      <Badge badgeContent={count.data ?? (count.isError ? '!' : 0)} color="error" max={99999} invisible={!count.isError && !count.data}>
        <IconButton
          ref={buttonRef}
          onClick={() => setOpen(value => !value)}
          size="small"
          aria-label={`Notifications, ${String(count.data ?? (count.isError ? 'count unavailable' : 0))} unread`}
          aria-expanded={open}
          aria-controls={panelId}
          sx={{ '&:focus-visible': { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}
        >
          <NotificationsNoneIcon fontSize="small" />
        </IconButton>
      </Badge>
      <Popper open={open} anchorEl={buttonRef.current} placement="bottom-end" disablePortal={false} sx={{ zIndex: theme => theme.zIndex.modal + 1 }}>
        <ClickAwayListener onClickAway={closeQuietly}>
          <Paper ref={panelRef} id={panelId} role="region" aria-label="Notifications" tabIndex={-1} elevation={8} sx={{ mt: 1, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
            {count.isError && (
              <Paper sx={{ p: 1 }} role="alert">
                Unread count unavailable{' '}
                <Button size="small" onClick={() => void count.refetch()} sx={{ '&:focus-visible': { outline: theme => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}>
                  Retry
                </Button>
              </Paper>
            )}
            <NotificationList key={open ? 'open' : 'closed'} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
